import { useState, useEffect } from 'react';
import { Plus, CreditCard as Edit2, Trash2, X, Save, Gauge, Shield, MapPin, Loader, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from './ConfirmModal';
import { useNotify } from './useNotify';
import { PhotoInput } from './PhotoInput';
import { apiClient } from '../lib/apiClient';
import { LocationPickerModal } from './LocationPickerModal';
import { PHOTO_BUCKETS, deletePhotoByUrl, uploadPhotoFile } from '../lib/photoUpload';
import { logAudit, AUDIT_ACTIONS, AUDIT_ENTITIES, computeChanges } from '../lib/auditLog';
import { submitAutoBugReport } from '../lib/autoBugReport';
import { describeError } from '../lib/errorUtils';
import { logEvent } from '../lib/telemetry';
import { readGpsLocation } from '../lib/googleMaps';
import type { GPSLocation } from '../lib/googleMaps';

interface WaterMeter {
  id: string;
  job_site_id: string;
  size: string;
  meter_number: string;
  location: string;
  is_rpz: boolean;
  gps_location: GPSLocation | null;
  photo_url: string | null;
  notes: string | null;
  created_at: string;
}

interface JobSiteWaterMetersProps {
  jobSiteId: string;
  jobSiteName: string;
  onDataChange?: () => void;
  /** View-only mode: add / edit / delete affordances are removed entirely. */
  readOnly?: boolean;
}

const SIZE_OPTIONS = [
  '3/4"', '1"', '1.5"', '2"', '2.5"', '3"', '4"', '6"', '8"', '10"', '12"',
];

export function JobSiteWaterMeters({ jobSiteId, onDataChange, readOnly = false }: JobSiteWaterMetersProps) {
  const { t } = useTranslation('common');
  const { t: tInsp } = useTranslation('inspection');
  const { notify, NotifyModal } = useNotify();
  const [meters, setMeters] = useState<WaterMeter[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    size: '1"',
    meter_number: '',
    location: '',
    is_rpz: false,
    gps_location: null as GPSLocation | null,
    notes: '',
    photo: null as File | null,
  });
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [gpsModalOpen, setGpsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; label: string } | null>(null);
  // Property's saved location — fallback map center for the GPS picker when a
  // device has no location yet and live GPS is blocked/unavailable in the field.
  const [propertyLocation, setPropertyLocation] = useState<GPSLocation | null>(null);

  useEffect(() => {
    loadMeters();
    loadPropertyLocation();
  }, [jobSiteId]);

  const loadPropertyLocation = async () => {
    try {
      const js = await apiClient.get<{ gps_location: unknown }>(`/api/job-sites/${jobSiteId}`);
      setPropertyLocation(readGpsLocation(js?.gps_location));
    } catch {
      setPropertyLocation(null);
    }
  };

  const loadMeters = async () => {
    try {
      const all = await apiClient.get<WaterMeter[]>('/api/water-meters');
      const filtered = all
        .filter((m) => m.job_site_id === jobSiteId)
        .sort((a, b) => a.created_at.localeCompare(b.created_at));
      setMeters(filtered);
    } catch (err) {
      console.error('Error loading water meters:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ size: '1"', meter_number: '', location: '', is_rpz: false, gps_location: null, notes: '', photo: null });
    setPhotoPreview(null);
    setSaveError(null);
    setIsAdding(false);
    setEditingId(null);
  };

  const handleAdd = () => {
    if (readOnly) return;
    resetForm();
    setIsAdding(true);
  };

  const handleEdit = (meter: WaterMeter) => {
    if (readOnly) return;
    setEditingId(meter.id);
    setIsAdding(false);
    setFormData({
      size: meter.size,
      meter_number: meter.meter_number,
      location: meter.location || '',
      is_rpz: meter.is_rpz || false,
      gps_location: meter.gps_location || null,
      notes: meter.notes || '',
      photo: null,
    });
    setPhotoPreview(meter.photo_url);
  };

  const uploadPhoto = async (meterId: string, file: File): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const path = `${meterId}-${Date.now()}.${fileExt}`;
      return await uploadPhotoFile(file, PHOTO_BUCKETS.WATER_METER, path);
    } catch (err) {
      console.error('Error uploading photo:', err);
      notify(t('status.errorTitle'), tInsp('waterMeters.errorUploadPhoto'), 'danger');
      return null;
    }
  };

  const deleteMeterPhoto = async (meterId: string) => {
    try {
      const oldUrl = meters.find((m) => m.id === meterId)?.photo_url ?? null;
      await apiClient.put(`/api/water-meters/${meterId}`, { photo_url: null });
      await deletePhotoByUrl(oldUrl);
      setPhotoPreview(null);
      setFormData((fd) => ({ ...fd, photo: null }));
      await loadMeters();
    } catch (err) {
      console.error('Error deleting water meter photo:', err);
      notify(t('status.errorTitle'), tInsp('waterMeters.errorDeletePhoto'), 'danger');
    }
  };

  const handleSave = async () => {
    if (readOnly) return;
    if (!formData.meter_number.trim()) {
      notify(t('status.noticeTitle'), t('waterMeters.meterNumberRequired'), 'warning');
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        size: formData.size,
        meter_number: formData.meter_number.trim(),
        location: formData.location.trim() || '',
        is_rpz: formData.is_rpz,
        gps_location: formData.gps_location,
        notes: formData.notes.trim() || null,
      };

      if (editingId) {
        let photoUrl = meters.find((m) => m.id === editingId)?.photo_url;
        if (formData.photo) {
          const uploadedUrl = await uploadPhoto(editingId, formData.photo);
          if (uploadedUrl) photoUrl = uploadedUrl;
        }
        await apiClient.put(`/api/water-meters/${editingId}`, { ...payload, photo_url: photoUrl });
        void logEvent('inspection_device_updated', `Updated water meter ${payload.meter_number}`, {
          metadata: { device_type: 'water_meter', device_id: editingId, label: payload.meter_number, job_site_id: jobSiteId },
        });
        const editingMeter = meters.find((m) => m.id === editingId);
        logAudit({
          action: AUDIT_ACTIONS.UPDATE,
          entityType: AUDIT_ENTITIES.WATER_METER,
          entityId: editingId,
          entityLabel: payload.meter_number,
          parentEntityType: AUDIT_ENTITIES.PROPERTY,
          parentEntityId: jobSiteId,
          details: computeChanges(
            { size: editingMeter?.size, meter_number: editingMeter?.meter_number, location: editingMeter?.location, is_rpz: editingMeter?.is_rpz, notes: editingMeter?.notes, photo_url: editingMeter?.photo_url },
            { size: payload.size, meter_number: payload.meter_number, location: payload.location, is_rpz: payload.is_rpz, notes: payload.notes, photo_url: photoUrl },
          ),
          pageContext: 'job-sites',
        });
      } else {
        const newMeter = await apiClient.post<WaterMeter>('/api/water-meters', { ...payload, job_site_id: jobSiteId });

        if (formData.photo && newMeter) {
          const photoUrl = await uploadPhoto(newMeter.id, formData.photo);
          if (photoUrl) {
            await apiClient.put(`/api/water-meters/${newMeter.id}`, { photo_url: photoUrl });
          }
        }
        if (newMeter) {
          void logEvent('inspection_device_added', `Added water meter ${payload.meter_number}`, {
            metadata: { device_type: 'water_meter', device_id: newMeter.id, label: payload.meter_number, job_site_id: jobSiteId },
          });
          logAudit({
            action: AUDIT_ACTIONS.CREATE,
            entityType: AUDIT_ENTITIES.WATER_METER,
            entityId: newMeter.id,
            entityLabel: payload.meter_number,
            parentEntityType: AUDIT_ENTITIES.PROPERTY,
            parentEntityId: jobSiteId,
            details: { size: payload.size, meter_number: payload.meter_number, location: payload.location, is_rpz: payload.is_rpz },
            pageContext: 'job-sites',
          });
        }
      }

      resetForm();
      await loadMeters();
      onDataChange?.();
    } catch (err: unknown) {
      console.error('Error saving water meter:', err);
      const described = describeError(err);
      setSaveError(described.message);
      const errorPayload = {
        size: formData.size,
        meter_number: formData.meter_number.trim(),
        location: formData.location.trim(),
        is_rpz: formData.is_rpz,
      };
      logAudit({
        action: editingId ? AUDIT_ACTIONS.UPDATE : AUDIT_ACTIONS.CREATE,
        entityType: AUDIT_ENTITIES.WATER_METER,
        entityLabel: `FAILED: ${formData.meter_number.trim()}`,
        parentEntityType: AUDIT_ENTITIES.PROPERTY,
        parentEntityId: jobSiteId,
        details: {
          error: described.message,
          status: described.status,
          code: described.code,
          online: navigator.onLine,
          payload: errorPayload,
        },
        pageContext: 'job-sites',
      });
      submitAutoBugReport({
        title: `Water meter save failed: ${formData.meter_number.trim()}`,
        description: [
          `Action: ${editingId ? 'Update' : 'Create'} water meter`,
          `Meter: ${formData.meter_number.trim()}`,
          `Property ID: ${jobSiteId}`,
          `Online: ${navigator.onLine}`,
          described.status !== undefined ? `Status: ${described.status}` : null,
          described.code !== undefined ? `Code: ${described.code}` : null,
          `Error: ${described.message}`,
        ].filter(Boolean).join('\n'),
        errorDetails: {
          error: described.message,
          name: described.name,
          stack: described.stack,
          status: described.status,
          code: described.code,
          raw: described.raw,
          online: navigator.onLine,
          editingId,
          jobSiteId,
          payload: errorPayload,
          timestamp: new Date().toISOString(),
          url: window.location.href,
        },
        pageContext: 'job-sites',
        priority: 'high',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (readOnly) return;
    if (!deleteConfirm) return;
    try {
      await apiClient.del(`/api/water-meters/${deleteConfirm.id}`);
      void logEvent('inspection_device_deleted', `Deleted water meter ${deleteConfirm.label}`, {
        metadata: { device_type: 'water_meter', device_id: deleteConfirm.id, label: deleteConfirm.label, job_site_id: jobSiteId },
      });
      logAudit({
        action: AUDIT_ACTIONS.DELETE,
        entityType: AUDIT_ENTITIES.WATER_METER,
        entityId: deleteConfirm.id,
        entityLabel: deleteConfirm.label,
        parentEntityType: AUDIT_ENTITIES.PROPERTY,
        parentEntityId: jobSiteId,
        pageContext: 'job-sites',
      });
      setDeleteConfirm(null);
      await loadMeters();
      onDataChange?.();
    } catch (err) {
      console.error('Error deleting water meter:', err);
    }
  };

  const handleSetGPS = () => setGpsModalOpen(true);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader size={24} className="animate-spin text-theme-secondary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-theme-primary">{tInsp('waterMeters.heading')}</h3>
        {!readOnly && !isAdding && !editingId && (
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <Plus size={16} />
            {tInsp('waterMeters.addMeter')}
          </button>
        )}
      </div>

      {!readOnly && (isAdding || editingId) && (
        <div className="bg-subtle border border-line rounded-lg p-4">
          <h4 className="text-sm font-semibold text-theme-primary mb-3">
            {editingId ? tInsp('waterMeters.editTitle') : tInsp('waterMeters.newTitle')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-theme-primary mb-1">{tInsp('waterMeters.sizeRequired')}</label>
              <select
                value={formData.size}
                onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-theme-primary mb-1">{tInsp('waterMeters.meterNumberLabel')}</label>
              <input
                type="text"
                value={formData.meter_number}
                onChange={(e) => setFormData({ ...formData, meter_number: e.target.value })}
                placeholder={tInsp('waterMeters.meterNumberPlaceholder')}
                className="w-full px-3 py-2 text-sm border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-theme-primary mb-1">{tInsp('waterMeters.location')}</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder={tInsp('waterMeters.locationPlaceholder')}
                className="w-full px-3 py-2 text-sm border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-theme-primary mb-1">{tInsp('waterMeters.notes')}</label>
              <input
                type="text"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={tInsp('waterMeters.notesPlaceholder')}
                className="w-full px-3 py-2 text-sm border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <label className="flex items-center gap-2.5 mt-3 p-2.5 rounded-lg border border-line bg-surface cursor-pointer hover:bg-subtle transition-colors">
            <input
              type="checkbox"
              checked={formData.is_rpz}
              onChange={(e) => setFormData({ ...formData, is_rpz: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <Shield size={16} className="text-blue-600" />
            <div>
              <span className="text-xs font-medium text-theme-primary">{tInsp('waterMeters.rpzLabel')}</span>
              <p className="text-xs text-theme-secondary">{tInsp('waterMeters.rpzHint')}</p>
            </div>
          </label>
          <div className="mt-3">
            <label className="block text-xs font-medium text-theme-primary mb-1">{tInsp('gps.location')}</label>
            {formData.gps_location ? (
              <div className="flex items-center gap-3 p-2.5 rounded-lg border border-line bg-subtle">
                <MapPin size={16} className="text-green-600 shrink-0" />
                <div className="text-xs text-theme-secondary flex-1">
                  <span>{formData.gps_location.latitude.toFixed(6)}, {formData.gps_location.longitude.toFixed(6)}</span>
                </div>
                <button
                  type="button"
                  onClick={handleSetGPS}
                  className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  {tInsp('gps.update')}
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, gps_location: null })}
                  className="text-xs px-2 py-1 bg-subtle text-theme-secondary rounded hover:bg-subtle transition-colors"
                >
                  {tInsp('gps.remove')}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleSetGPS}
                className="flex items-center gap-1.5 px-3 py-2 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <MapPin size={14} />
                {tInsp('gps.setLocation')}
              </button>
            )}
          </div>
          <LocationPickerModal
            open={gpsModalOpen}
            existing={formData.gps_location}
            fallback={propertyLocation}
            onSave={(loc) => setFormData((prev) => ({ ...prev, gps_location: loc }))}
            onClose={() => setGpsModalOpen(false)}
          />
          <div className="mt-3">
            <PhotoInput
              label={tInsp('waterMeters.photoLabel')}
              preview={photoPreview}
              onFileChange={(file, preview) => {
                setFormData((fd) => ({ ...fd, photo: file }));
                setPhotoPreview(preview);
              }}
              onDelete={editingId
                ? () => deleteMeterPhoto(editingId)
                : () => { setPhotoPreview(null); setFormData((fd) => ({ ...fd, photo: null })); }}
            />
          </div>
          {saveError && (
            <div className="mt-3 flex items-start gap-2.5 p-3 bg-red-50 border border-red-200 dark:bg-red-500/10 dark:border-red-500/30 rounded-lg">
              <AlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-red-800 dark:text-red-300">{t('waterMeters.saveFailed')}</p>
                <p className="text-xs text-red-600 dark:text-red-400 mt-0.5 break-words">{saveError}</p>
              </div>
              <button
                onClick={() => setSaveError(null)}
                className="p-0.5 text-red-400 hover:text-red-600 dark:hover:text-red-400 transition-colors shrink-0"
              >
                <X size={14} />
              </button>
            </div>
          )}
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Save size={14} />
              {saving ? t('status.saving') : t('actions.save')}
            </button>
            <button
              onClick={resetForm}
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-subtle text-theme-primary rounded-lg hover:bg-subtle transition-colors"
            >
              <X size={14} />
              {t('actions.cancel')}
            </button>
          </div>
        </div>
      )}

      {meters.length === 0 && !isAdding ? (
        <div className="text-center py-12 bg-subtle rounded-lg border border-line">
          <Gauge size={32} className="mx-auto text-theme-secondary mb-2" />
          <p className="text-sm font-medium text-theme-secondary">{tInsp('waterMeters.empty')}</p>
          <p className="text-xs text-theme-secondary mt-1">{tInsp('waterMeters.emptyHint')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {meters.map((meter) => (
            <div
              key={meter.id}
              className="flex items-center justify-between bg-surface border border-line rounded-lg p-3 hover:border-line transition-colors"
            >
              <div className="flex items-center gap-3">
                {meter.photo_url ? (
                  <img
                    src={meter.photo_url}
                    alt={meter.meter_number}
                    className="w-12 h-12 object-cover rounded-lg border border-line"
                  />
                ) : (
                  <div className="w-12 h-12 bg-blue-50 dark:bg-blue-500/10 rounded-lg flex items-center justify-center">
                    <Gauge size={18} className="text-blue-600 dark:text-blue-400" />
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-theme-primary">{meter.meter_number}</span>
                    <span className="text-xs px-2 py-0.5 bg-subtle text-theme-secondary rounded">{meter.size}</span>
                    {meter.is_rpz && (
                      <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300 rounded flex items-center gap-1">
                        <Shield size={10} />
                        {tInsp('waterMeters.rpzBadge')}
                      </span>
                    )}
                  </div>
                  {meter.location && (
                    <p className="text-xs text-theme-secondary mt-0.5">{meter.location}</p>
                  )}
                  {meter.gps_location && (
                    <a
                      href={`https://www.google.com/maps?q=${meter.gps_location.latitude},${meter.gps_location.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-theme-primary mt-0.5"
                    >
                      <MapPin size={10} />
                      {meter.gps_location.latitude.toFixed(6)}, {meter.gps_location.longitude.toFixed(6)}
                    </a>
                  )}
                  {meter.notes && (
                    <p className="text-xs text-theme-secondary mt-0.5">{meter.notes}</p>
                  )}
                </div>
              </div>
              {!readOnly && (
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEdit(meter)}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-500/10 rounded-lg transition-colors"
                    title={t('actions.edit')}
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm({ id: meter.id, label: meter.meter_number })}
                    className="p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 rounded-lg transition-colors"
                    title={t('actions.delete')}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmModal
        isOpen={deleteConfirm !== null}
        title={tInsp('waterMeters.deleteTitle')}
        message={tInsp('waterMeters.deleteMessage', { label: deleteConfirm?.label ?? '' })}
        confirmText={t('actions.delete')}
        cancelText={t('actions.cancel')}
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm(null)}
      />

      {NotifyModal}
    </div>
  );
}
